import { Component, inject, model, signal } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ChangePasswordService } from '../../services/change-password.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, ValidationErrors, ValidatorFn, AbstractControl } from '@angular/forms';
import { UserService } from '../../../data/services/user/user.service';
import { InputTextComponent } from '../common/input-text/input-text.component';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ChangeCurrentUserPasswordReqBody, ChangePasswordRequestBody } from '../../../../shared/models/auth.model';


@Component({
  selector: 'app-change-password-dialog',
  imports: [DialogModule, ReactiveFormsModule, InputTextComponent, ButtonModule],
  providers: [ConfirmationService],
  template: `
        <p-dialog [draggable]="false" header="Cambia Password" [modal]="true" [(visible)]="showChangePasswordDialog"
         [style]="{ 'width': '35rem', margin: '2rem' }" [appendTo]="'body'">
         <span><strong>Utente:</strong> {{userName()}}</span>
          <form [formGroup]="passwordForm" (ngSubmit)="onSubmit()" class="w-full flex flex-col gap-8">
            <div class="flex flex-col gap-6 pt-5">
            @if(!this.userId){
                          <app-input-text
              htmlId="currentPassword"
              type="password"
              label="Password Attuale *"
              formControlName="currentPassword"
              [required]="true"
              classInputText="w-full"
            ></app-input-text>
            }  
            <app-input-text
              htmlId="newPassword"
              type="password"
              label="Nuova Password *"
              formControlName="newPassword"
              [required]="true"
              classInputText="w-full"
            ></app-input-text>
            @if (newPasswordError) {
              <div class="text-red-600 text-xs mb-1">
                {{ newPasswordError }}
              </div>
            }
            <app-input-text
              htmlId="confirmPassword"
              type="password"
              label="Conferma Nuova Password *"
              formControlName="confirmPassword"
              [required]="true"
              classInputText="w-full"
            ></app-input-text>
            @if (passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched) {
              <div class="text-red-600 text-xs">
                Le password non coincidono
              </div>
            }
            </div>
            <div class="flex justify-end gap-2 mt-4">
              <button type="button" pButton label="Annulla" class="p-button-text" (click)="closeDialog()" ></button>
              <button type="submit" pButton label="Conferma" [disabled]="passwordForm.invalid" ></button>
            </div>
          </form>
          @if (errorMessage()) {
            <div class="text-red-600 text-sm mt-2">{{ errorMessage() }}</div>
          }

          
        </p-dialog>
  `,
})
export class ChangePasswordDialogComponent {

  protected showChangePasswordDialog = model<boolean>(false);
  private _changePasswordService = inject(ChangePasswordService);
  private _userService = inject(UserService);
  private _messageService = inject(MessageService);

  passwordForm: FormGroup = new FormGroup({});
  loading = signal(false);
  errorMessage = signal<string | null>(null);

  protected userId: string | null = null;
  protected userName = signal<string | null>(null);

  protected get newPasswordError(): string | null {
    const control = this.passwordForm.get('newPassword');
    if (control?.invalid && control?.touched) {
      if (control.hasError('required')) {
        return 'La nuova password è obbligatoria.';
      }
      else if (control.hasError('minlength')) {
        const errors = control.errors!['minlength'];

        if (control.hasError('pattern')) {
          return `La password deve essere lunga almeno ${errors.requiredLength} e contenere almeno una cifra.`;
        }

        return `La password deve essere lunga almeno ${errors.requiredLength} caratteri.`;
      }
      else if (control.hasError('pattern')) {
        return 'La password deve contenere almeno una cifra.';
      }
    }
    return null;
  }

  constructor() {
    this._changePasswordService.isChangePassword$.subscribe(({ isOpen, userName, id }) => {
      this.showChangePasswordDialog.set(isOpen);

      if (id) {
        this.userId = id;
      } else {
        this.userId = null;
      }

      this.initForm();
      if (userName) {
        this.userName.set(userName);
      }

    });
  }

  protected initForm() {
    const isCurrentUser = !this.userId;

    const controls: { [key: string]: AbstractControl } = {
      newPassword: new FormControl('', [Validators.required, Validators.minLength(6), Validators.pattern(/\d/)]),
      confirmPassword: new FormControl('', [Validators.required, Validators.minLength(6)])
    };

    if (isCurrentUser) {
      controls['currentPassword'] = new FormControl('', [Validators.required]);
    }

    this.passwordForm = new FormGroup(controls, { validators: this.passwordsMatchValidator });
    this.errorMessage.set(null);
  }

  private passwordsMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  };

  protected onSubmit() {
    // if (this.passwordForm.invalid || !this.userId) {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    if (this.userId) {
      this.changePassword();
    } else {
      this.changeCurrentUserPassword();
    }
  }
  changeCurrentUserPassword() {
    const changeCurrentUserPassReqBody: ChangeCurrentUserPasswordReqBody = {
      currentPassword: this.passwordForm.get('currentPassword')?.value,
      newPassword: this.passwordForm.get('newPassword')?.value,
      confirmNewPassword: this.passwordForm.get('confirmPassword')?.value
    }
    this._userService.changeCurrentUserPassword(changeCurrentUserPassReqBody)
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Password aggiornata con successo!",
          });
          this.closeDialog();
        },
        error: (err) => {
          console.log('Error changing password:', err);
          this.errorMessage.set('Errore durante il cambio password. Riprova.');
        }
      });

  }

  private changePassword() {
    const changePassReqBody: ChangePasswordRequestBody = {
      newPassword: this.passwordForm.get('newPassword')?.value,
    }
    this._userService.resetUserPassword(changePassReqBody, this.userId!)
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Password aggiornata con successo!",
          });
          this.closeDialog();
        },
        error: (err) => {
          console.log('Error changing password:', err);
          this.errorMessage.set('Errore durante il cambio password. Riprova.');
        }
      });
  }

  protected closeDialog() {
    this.showChangePasswordDialog.set(false);
    this.passwordForm.reset();
    this.errorMessage.set(null);
  }
}
